import React from "react";
import { Modal, Switch } from "antd";
import { Button } from "components/Button";
import { useMediaQuery } from "react-responsive";

// Offset the modal wrapper by the same amount the page content is offset by
// the sidebar, so `centered` modals visually center within the visible content
// area (not the full viewport). Keeps the modal aligned regardless of whether
// the sidebar is expanded (250px) or collapsed (60px); no offset on mobile
// where the sidebar is hidden.
const SHELL_WRAP_STYLES = `
  @media (min-width: 992px) {
    .form-modal-shell-wrap { padding-left: 250px; }
    body.mini-sidebar .form-modal-shell-wrap { padding-left: 60px; }
  }
`;

/**
 * Consistent modal shell for create/edit forms.
 *
 * Renders:
 *   - Header with title + optional subtitle, with a clear close icon.
 *   - Scrollable body (slot for form fields).
 *   - Sticky-feel footer with Cancel + primary action button.
 *
 * Replaces the old `.submit-section` / `.submit-btn` pattern that floated
 * the action button outside the modal on some screens.
 */
const FormModalShell = ({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  loading = false,
  disabled = false,
  width = 640,
  bodyMaxHeight = "65vh",
  children,
}) => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (typeof onSubmit === "function") onSubmit(e);
  };

  return (
    <>
      <style>{SHELL_WRAP_STYLES}</style>
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? "100%" : width}
      centered
      maskClosable={false}
      mask={false}
      destroyOnClose
      wrapClassName="form-modal-shell-wrap"
      closeIcon={
        <i
          className="la la-times"
          style={{ fontSize: 18, color: "#6b7280" }}
          aria-hidden="true"
        />
      }
      title={
        <div style={{ paddingBottom: 4 }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </div>
          {subtitle ? (
            <div
              style={{
                fontSize: 12.5,
                color: "#6b7280",
                marginTop: 4,
                fontWeight: 400,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
      }
      bodyStyle={{ padding: "16px 0 0" }}
    >
      <form onSubmit={handleFormSubmit} noValidate>
        <div
          style={{
            maxHeight: bodyMaxHeight,
            overflowY: "auto",
            padding: "0 4px",
          }}
        >
          {children}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid #f1f2f4",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn-light"
            style={{
              minWidth: 96,
              borderRadius: 8,
              fontWeight: 500,
            }}
          >
            {cancelLabel}
          </button>
          <Button
            type="submit"
            loading={loading}
            disabled={disabled || loading}
            className="btn btn-primary"
            label={submitLabel}
            style={{
              minWidth: 120,
              borderRadius: 8,
              fontWeight: 600,
            }}
          />
        </div>
      </form>
    </Modal>
    </>
  );
};

/**
 * Inline status toggle row — replaces the awkward "Status" labelled field
 * that previously sat inside an `.input-block`. Visual: a single full-width
 * row with label on the left, switch + state text on the right.
 */
export const StatusToggleRow = ({
  value,
  onChange,
  label = "Status",
  helper,
  onLabel = "Active",
  offLabel = "Inactive",
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 14px",
      background: "#fafbfc",
      border: "1px solid #f1f2f4",
      borderRadius: 10,
      marginTop: 4,
      marginBottom: 4,
    }}
  >
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
        {label}
      </div>
      {helper ? (
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
          {helper}
        </div>
      ) : null}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Switch checked={!!value} onChange={onChange} aria-label={label} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: value ? "#15803d" : "#6b7280",
          minWidth: 56,
          textAlign: "left",
        }}
      >
        {value ? onLabel : offLabel}
      </span>
    </div>
  </div>
);

export default FormModalShell;
